import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import CreateTeamModalShell from './CreateTeamModalShell';
import type { ICreateNewTeamByPlayerRequest } from '../types/myTeam';
import type { IMyTeam } from '../types/myTeam';
import { normalizeImageFileTypeForApi } from '../../../helper/imageFileTypeForApi';

interface CreateTeamFlexModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateTeamFlexFormData) => void | Promise<void>;
  /** When provided, form opens in edit mode with values pre-filled */
  initialData?: IMyTeam | null;
}

export interface CreateTeamFlexFormData {
  teamName: string;
  teamCode?: string;
  description?: string;
  image?: ICreateNewTeamByPlayerRequest['image'];
  approveNewPlayers: boolean;
}

function CreateTeamFlexModal({ open, onOpenChange, onSubmit, initialData }: CreateTeamFlexModalProps) {
  const [teamName, setTeamName] = useState('');
  const [teamCode, setTeamCode] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamImageName, setTeamImageName] = useState('');
  const [approveNewPlayers, setApproveNewPlayers] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [teamImageFile, setTeamImageFile] = useState<File | null>(null);

  const isEditMode = !!initialData;

  useEffect(() => {
    if (open && initialData) {
      setTeamName(initialData.teamName ?? '');
      setTeamCode(initialData.teamCode ?? '');
      setTeamDescription(initialData.description ?? '');
      setTeamImageName(initialData.teamLogo ?? '');
      setTeamImageFile(null);
      setApproveNewPlayers(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open, initialData]);

  const resetForm = () => {
    setTeamName('');
    setTeamCode('');
    setTeamDescription('');
    setTeamImageName('');
    setTeamImageFile(null);
    setApproveNewPlayers(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setTeamImageFile(file ?? null);
    setTeamImageName(file?.name ?? '');
  };

  const convertFileToImagePayload = (file: File) =>
    new Promise<NonNullable<ICreateNewTeamByPlayerRequest['image']>>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          reject(new Error('Unable to read image file.'));
          return;
        }

        const base64Image = reader.result.split(',')[1];

        if (!base64Image) {
          reject(new Error('Unable to encode image file.'));
          return;
        }

        resolve({
          fileType: normalizeImageFileTypeForApi(file.type, file.name),
          base64Image,
        });
      };

      reader.onerror = () => reject(reader.error ?? new Error('Unable to read image file.'));
      reader.readAsDataURL(file);
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let imagePayload: CreateTeamFlexFormData['image'];

    if (teamImageFile) {
      try {
        imagePayload = await convertFileToImagePayload(teamImageFile);
      } catch (error) {
        console.error('Image conversion failed', error);
        toast.error('Unable to process the team image. Please try another file.');
        return;
      }
    }

    await onSubmit({
      teamName: teamName.trim(),
      teamCode: teamCode.trim() || undefined,
      description: teamDescription.trim() || undefined,
      image: imagePayload,
      approveNewPlayers,
    });
  };

  return (
    <CreateTeamModalShell
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          resetForm();
        }
      }}
      title={isEditMode ? 'Edit Team' : 'Create a Team'}
    >
      <form onSubmit={handleSubmit} className="create-team-form">
        <div className="create-team-row">
          <div className="create-team-field create-team-field-image">
            <label>
              Team Image<span className="required">*</span>
            </label>
            <div className="create-team-image-input">
              <input type="text" placeholder="Add image/logo" value={teamImageName} readOnly />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="create-team-file-input"
                onChange={handleImageUpload}
              />
              <button
                type="button"
                className="create-team-upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                <span className="create-team-upload-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path
                      d="M10 12.5V4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6.75 7.75L10 4.5L13.25 7.75"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.5 11.75V13.25C4.5 14.2165 5.2835 15 6.25 15H13.75C14.7165 15 15.5 14.2165 15.5 13.25V11.75"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>Upload</span>
              </button>
            </div>
          </div>

          <div className="create-team-field create-team-field-compact">
            <label>
              Team Name<span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              required
            />
          </div>

          <div className="create-team-field create-team-field-compact">
            <label>
              Team Code<span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter Team Code"
              value={teamCode}
              onChange={(e) => setTeamCode(e.target.value)}
            />
          </div>
        </div>

        <div className="create-team-field create-team-field-description">
          <label>
            Team Description<span className="required">*</span>
          </label>
          <textarea
            placeholder="Description"
            value={teamDescription}
            onChange={(e) => setTeamDescription(e.target.value)}
            rows={5}
          />
        </div>

        <div className="create-team-field create-team-toggle">
          <div className="create-team-toggle-copy">
            <label>Approve New Players</label>
            <p className="create-team-toggle-desc">
              When turned on, captains must approve a player to join the team.
            </p>
          </div>
          <button
            type="button"
            className={`create-team-switch ${approveNewPlayers ? 'on' : ''}`}
            onClick={() => setApproveNewPlayers(!approveNewPlayers)}
            role="switch"
            aria-checked={approveNewPlayers}
          >
            <span className="create-team-switch-slider" />
          </button>
        </div>

        <div className="create-team-footer">
          <button type="submit" className="create-team-submit">
            {isEditMode ? 'Save Changes' : 'Create Team'}
          </button>
        </div>
      </form>
    </CreateTeamModalShell>
  );
}

export default CreateTeamFlexModal;
